import { arrayMove } from '@dnd-kit/sortable';
import { updateEpicsOrder, updateTaskOrder } from '../../api/workspace';
import { queryClient } from '../../queryClient';
import Workspace from '../../types/Workspace';
import { Epic } from '../../types/Epic';

export async function updateSpaceTasksOrder(spaceId: number, tasksOrder: number[], activeId?: number, overId?: number) {
  const space: Workspace | undefined = queryClient.getQueryData(['workspace', spaceId]);
  const oldTaskOrder = space?.TaskOrder[0]?.taskIds;
  let newTaskOrder: number[] = [];
  if (activeId && overId && oldTaskOrder?.includes(activeId) && oldTaskOrder?.includes(overId)) {
    const activeIndex = oldTaskOrder.findIndex((i) => i === activeId);
    const overIndex = oldTaskOrder.findIndex((i) => i === overId);

    newTaskOrder = arrayMove(oldTaskOrder, activeIndex, overIndex);
  } else {
    newTaskOrder = [
      ...(oldTaskOrder ? oldTaskOrder.filter((taskId) => !tasksOrder.includes(taskId)) : []),
      ...tasksOrder,
    ];
  }

  // Update the local cache first so a mutation that races the network
  // round-trip (e.g. a property change right after this drag) re-sorts
  // against the new order instead of the stale pre-drag one.
  updateTasksOrderLocally(spaceId, newTaskOrder);

  await updateTaskOrder(spaceId, newTaskOrder);
}

export async function addToSpaceTasksOrder(spaceId: number, taskId: number, addToTop = false) {
  const space: Workspace | undefined = queryClient.getQueryData(['workspace', spaceId]);
  const oldTaskOrder = space?.TaskOrder[0]?.taskIds || [];
  const newTaskOrder: number[] = [...(oldTaskOrder || [])];
  if (addToTop) {
    newTaskOrder.unshift(taskId);
  } else {
    newTaskOrder.push(taskId);
  }

  // Update the local cache first so back-to-back creates each see the
  // appended state when they read the cache, instead of all racing on
  // the same pre-creation array.
  updateTasksOrderLocally(spaceId, newTaskOrder);

  await updateTaskOrder(spaceId, newTaskOrder);
}

export async function insertInSpaceTasksOrder(spaceId: number, taskId: number, beforeTaskId?: number) {
  const space: Workspace | undefined = queryClient.getQueryData(['workspace', spaceId]);
  const oldTaskOrder = space?.TaskOrder[0]?.taskIds || [];
  const newTaskOrder: number[] = [...oldTaskOrder];

  if (beforeTaskId) {
    const index = newTaskOrder.indexOf(beforeTaskId);
    if (index !== -1) {
      newTaskOrder.splice(index, 0, taskId);
    } else {
      // beforeTaskId not in array (untracked task) — put new task at the front
      // so it sorts before all other tracked tasks
      newTaskOrder.unshift(taskId);
    }
  } else {
    newTaskOrder.push(taskId);
  }

  // Update the local cache first, matching addToSpaceTasksOrder and
  // updateSpaceTasksOrder. Doing it after the round trip left anything that
  // read the order in between (the modal closing, a socket echo, a second
  // create) looking at the pre-insert array, so the new task rendered in the
  // wrong slot until something else refreshed it. (#3177)
  updateTasksOrderLocally(spaceId, newTaskOrder);

  await updateTaskOrder(spaceId, newTaskOrder);
}

export async function removeFromSpaceTasksOrder(spaceId: number, taskId: number) {
  const space: Workspace | undefined = queryClient.getQueryData(['workspace', spaceId]);
  const oldTaskOrder = space?.TaskOrder[0].taskIds;
  const newTaskOrder = oldTaskOrder?.filter((currTaskId) => currTaskId !== taskId) ?? [];

  await updateTaskOrder(spaceId, newTaskOrder);

  updateTasksOrderLocally(spaceId, newTaskOrder);
}

export function updateTasksOrderLocally(spaceId: number, tasksOrder: number[]) {
  queryClient.setQueryData(['workspace', spaceId], (oldData: Workspace) => {
    const oldTaskOrder = oldData.TaskOrder[0];
    return {
      ...oldData,
      TaskOrder: [{ ...oldTaskOrder, taskIds: tasksOrder }],
    };
  });
}

export async function insertInSpaceEpicsOrder(spaceId: number, epicId: number, beforeEpicId?: number) {
  const epics: Epic[] | undefined = queryClient.getQueryData(['epics', spaceId]);
  const oldOrder = epics?.map((e) => e.id) || [];
  const newOrder: number[] = [...oldOrder];

  if (beforeEpicId) {
    const index = newOrder.indexOf(beforeEpicId);
    if (index !== -1) {
      newOrder.splice(index, 0, epicId);
    } else {
      newOrder.push(epicId);
    }
  } else {
    newOrder.push(epicId);
  }

  await updateSpaceEpicsOrder(spaceId, newOrder);
}

export async function updateSpaceEpicsOrder(spaceId: number, tasksOrder: number[]) {
  await updateEpicsOrder(spaceId, tasksOrder);

  queryClient.setQueryData(['epics', spaceId], (oldData: Epic[]) => {
    return oldData
      .map((epic) => {
        if (tasksOrder.includes(epic.id)) {
          return { ...epic, orderIndex: tasksOrder.indexOf(epic.id) };
        }
        return epic;
      })
      .sort((a, b) => tasksOrder.indexOf(a.id) - tasksOrder.indexOf(b.id));
  });
}
